"use client";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { DisputeActionsPanel } from "@/components/admin/DisputeActionsPanel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Money } from "@/components/common/Money";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface DisputeData {
  dispute: any;
  allQuotes: any[];
  statusHistory: any[];
  hoursOpen: number;
}

export default function AdminDisputeDetailPage() {
  const navigate = useNavigate();
  const params = useParams();
  const disputeId = params.id as string;
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<DisputeData | null>(null);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate("/login");
      return;
    }

    fetchDisputeDetail();
  }, [user, authLoading, disputeId]);

  async function fetchDisputeDetail() {
    setLoading(true);
    try {
      const response = await fetch(`/api/admin/disputes/${disputeId}`);

      if (!response.ok) {
        toast.error("Failed to load dispute details");
        navigate("/admin/disputes");
        return;
      }

      const disputeData = await response.json();
      setData(disputeData);
    } catch (error) {
      console.error("Error fetching dispute:", error);
      toast.error("Failed to load dispute details");
      navigate("/admin/disputes");
    } finally {
      setLoading(false);
    }
  }

  if (authLoading || loading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="lg:col-span-2 h-[600px]" />
          <Skeleton className="h-[400px]" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { dispute, allQuotes, statusHistory, hoursOpen } = data;

  const requestedResolution = dispute.meta?.requestedResolution;
  const activeQuote = allQuotes.find((q: any) => q.isActive);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <PageHeader
        title={`Dispute: ${dispute.order.publicId}`}
        subtitle={`Opened ${formatDistanceToNow(new Date(dispute.openedAt), {
          addSuffix: true,
        })}`}
        actions={
          <Button asChild variant="outline">
            <Link to="/admin/disputes">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Disputes
            </Link>
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Dispute Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Status</p>
                  <Badge variant="outline">{dispute.status.replace(/_/g, ' ')}</Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Resolution Requested
                  </p>
                  <Badge variant="outline">
                    {requestedResolution === "DISCOUNT" && "Discount"}
                    {requestedResolution === "REVISE_QUOTE" && "Quote Revision"}
                    {requestedResolution === "RETURN" && "Return"}
                    {!requestedResolution && "N/A"}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Time Open
                  </p>
                  <p className="font-medium">{hoursOpen} hours</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Order Status</p>
                  <Badge variant="outline">{dispute.order.status.replace(/_/g, ' ')}</Badge>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground">Reason</p>
                <p className="mt-1">{dispute.reason}</p>
              </div>

              {dispute.customerNote && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Customer Note
                  </p>
                  <p className="mt-1">{dispute.customerNote}</p>
                </div>
              )}

              {dispute.supportNote && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Support Note
                  </p>
                  <p className="mt-1">{dispute.supportNote}</p>
                </div>
              )}

              {dispute.meta?.preferredReturnSlotId && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Customer's Preferred Return Slot
                  </p>
                  <p className="mt-1 text-sm">{dispute.meta.preferredReturnSlotId}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Customer Information</CardTitle>
                <Link
                  to={`/admin/customers/${dispute.order.customer.id}`}
                  className="text-sm text-primary hover:underline"
                >
                  View Profile
                </Link>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Name</p>
                <p>{dispute.order.customer.name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Customer ID
                </p>
                <p className="font-mono text-sm">{dispute.order.customer.publicId}</p>
              </div>
              {dispute.order.customer.phone && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Phone</p>
                  <p>{dispute.order.customer.phone}</p>
                </div>
              )}
              {dispute.order.customer.email && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Email</p>
                  <p>{dispute.order.customer.email}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Tabs defaultValue="items">
            <TabsList>
              <TabsTrigger value="items">Order Items</TabsTrigger>
              <TabsTrigger value="quotes">Quote History</TabsTrigger>
              <TabsTrigger value="shipments">Shipments</TabsTrigger>
              <TabsTrigger value="timeline">Timeline</TabsTrigger>
            </TabsList>

            <TabsContent value="items">
              <Card>
                <CardHeader>
                  <CardTitle>Order Items</CardTitle>
                </CardHeader>
                <CardContent>
                  {dispute.order.items.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No items</p>
                  ) : (
                    <div className="space-y-3">
                      {dispute.order.items.map((item: any) => (
                        <div key={item.id} className="flex justify-between p-3 border rounded-lg">
                          <div>
                            <p className="font-medium">{item.garmentType}</p>
                            {item.tagCode && (
                              <p className="text-sm text-muted-foreground font-mono">
                                {item.tagCode}
                              </p>
                            )}
                            <Badge variant="outline">{item.status.replace(/_/g, ' ')}</Badge>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground">Qty: {item.qty}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="quotes">
              <Card>
                <CardHeader>
                  <CardTitle>Quote History</CardTitle>
                </CardHeader>
                <CardContent>
                  {allQuotes.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No quotes</p>
                  ) : (
                    <div className="space-y-4">
                      {allQuotes.map((quote: any) => (
                        <div
                          key={quote.id}
                          className={`p-4 border rounded-lg ${
                            quote.isActive ? "border-primary" : ""
                          }`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium">Version {quote.versionNo}</p>
                              <Badge variant="outline">{quote.status.replace(/_/g, ' ')}</Badge>
                              {quote.isActive && (
                                <Badge className="ml-2" variant="default">
                                  Active
                                </Badge>
                              )}
                            </div>
                            <div className="text-right">
                              <Money amount={quote.total} currency={quote.currency} />
                            </div>
                          </div>
                          <div className="text-sm text-muted-foreground space-y-1">
                            <div className="flex justify-between">
                              <span>Subtotal:</span>
                              <Money amount={quote.subtotal} currency={quote.currency} />
                            </div>
                            <div className="flex justify-between">
                              <span>Tax:</span>
                              <Money amount={quote.tax} currency={quote.currency} />
                            </div>
                            <div className="flex justify-between">
                              <span>Delivery Fee:</span>
                              <Money amount={quote.deliveryFee} currency={quote.currency} />
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            Created {formatDistanceToNow(new Date(quote.createdAt), { addSuffix: true })}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="shipments">
              <Card>
                <CardHeader>
                  <CardTitle>Shipments</CardTitle>
                </CardHeader>
                <CardContent>
                  {dispute.order.shipments.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No shipments</p>
                  ) : (
                    <div className="space-y-3">
                      {dispute.order.shipments.map((shipment: any) => (
                        <div key={shipment.id} className="p-3 border rounded-lg">
                          <div className="flex justify-between items-start">
                            <div>
                              <Badge variant="outline">{shipment.type}</Badge>
                              <Badge
                                variant="outline"
                                className="ml-2"
                              >
                                {shipment.status.replace(/_/g, ' ')}
                              </Badge>
                              <p className="text-sm text-muted-foreground mt-2">
                                {new Date(shipment.slot.startAt).toLocaleDateString()} -{" "}
                                {new Date(shipment.slot.startAt).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </p>
                              {shipment.assignedRider && (
                                <p className="text-sm text-muted-foreground">
                                  Rider: {shipment.assignedRider.name}
                                </p>
                              )}
                            </div>
                            {shipment.otp && (
                              <div className="text-right">
                                <p className="text-xs text-muted-foreground">OTP</p>
                                <p className="font-mono font-bold">{shipment.otp}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="timeline">
              <Card>
                <CardHeader>
                  <CardTitle>Dispute Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  {statusHistory.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No history</p>
                  ) : (
                    <div className="space-y-3">
                      {statusHistory.map((entry: any) => (
                        <div key={entry.id} className="flex gap-3 pb-3 border-b last:border-0">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{entry.toStatus.replace(/_/g, ' ')}</Badge>
                              {entry.fromStatus && (
                                <>
                                  <span className="text-xs text-muted-foreground">from</span>
                                  <Badge variant="outline">{entry.fromStatus.replace(/_/g, ' ')}</Badge>
                                </>
                              )}
                            </div>
                            {entry.actor && (
                              <p className="text-sm text-muted-foreground mt-1">
                                by {entry.actor.name} ({entry.actor.role})
                              </p>
                            )}
                          </div>
                          <div className="text-right text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(entry.createdAt), {
                              addSuffix: true,
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-6">
          <DisputeActionsPanel disputeId={dispute.id} dispute={dispute} />

          <Card>
            <CardHeader>
              <CardTitle>Quick Links</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Link
                to={`/workshop/orders/${dispute.orderId}`}
                className="text-sm text-primary hover:underline block"
              >
                View Full Order →
              </Link>
              {activeQuote && (
                <Link
                  to={`/customer/orders/${dispute.orderId}/quote`}
                  className="text-sm text-primary hover:underline block"
                >
                  View Active Quote →
                </Link>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
